// src/components/proposal/ProductSynthesis.tsx
// Displays the synthesized Product Definition from deliverables analysis

import React, { useState } from 'react';
import {
  Sparkles,
  Box,
  Building2,
  Users,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Layers,
  Target,
  Shield,
  FileText,
  TrendingUp,
  Wrench,
  UserCheck,
  Gauge,
  Calendar,
  ClipboardCheck,
  BarChart3,
  ListChecks,
  Activity,
  Clock,
  Wand2,
  // New icons for product specification
  Package,
  Monitor,
  Route,
  Server,
  Link2,
  Repeat,
  Truck,
  Zap,
  ArrowRight,
  CircleDot,
} from 'lucide-react';

// Import types from deliverables.ts
import type { ProductDefinition } from '../../types/deliverables';
export type { ProductDefinition };

interface ProductSynthesisProps {
  productDefinition?: ProductDefinition | null;
  onRegenerate?: () => void;
  onEnhance?: () => void;
  isRegenerating?: boolean;
  isEnhancing?: boolean;
  className?: string;
}

// Helper functions for styling
const getDeliveryModelColor = (type: string) => {
  switch (type) {
    case 'build': return 'bg-bw-primary-tint text-bw-primary border-purple-200';
    case 'buy': return 'bg-bw-primary-tint text-bw-primary border-blue-200';
    case 'subscribe': return 'bg-success-bg text-success border-green-200';
    case 'hybrid': return 'bg-fit-moderate-bg text-fit-moderate border-amber-200';
    default: return 'bg-bw-surface-subtle text-bw-ink border-bw-border';
  }
};

const getDeliveryModelIcon = (type: string) => {
  switch (type) {
    case 'build': return '🔨';
    case 'buy': return '🛒';
    case 'subscribe': return '🔄';
    case 'hybrid': return '🔀';
    default: return '📦';
  }
};

const getBidRecommendationStyle = (rec: string) => {
  switch (rec) {
    case 'strong-bid': return { bg: 'bg-success-bg', border: 'border-green-200', text: 'text-success', icon: CheckCircle };
    case 'bid': return { bg: 'bg-bw-primary-tint', border: 'border-blue-200', text: 'text-bw-primary', icon: CheckCircle };
    case 'consider': return { bg: 'bg-fit-moderate-bg', border: 'border-amber-200', text: 'text-fit-moderate', icon: HelpCircle };
    case 'no-bid': return { bg: 'bg-urgent-bg', border: 'border-red-200', text: 'text-urgent', icon: XCircle };
    default: return { bg: 'bg-bw-surface-subtle', border: 'border-bw-border', text: 'text-bw-ink', icon: HelpCircle };
  }
};

const getComponentCategoryStyle = (category: string) => {
  switch (category) {
    case 'core': return 'bg-bw-primary-tint text-bw-primary';
    case 'feature': return 'bg-bw-primary-tint text-bw-primary';
    case 'service': return 'bg-success-bg text-success';
    case 'compliance': return 'bg-fit-moderate-bg text-fit-moderate';
    case 'reporting': return 'bg-slate-100 text-slate-700';
    default: return 'bg-bw-surface-subtle text-bw-ink';
  }
};

const getRiskImpactStyle = (impact: string) => {
  switch (impact) {
    case 'high': return 'bg-urgent-bg text-urgent';
    case 'medium': return 'bg-fit-moderate-bg text-fit-moderate';
    case 'low': return 'bg-success-bg text-success';
    default: return 'bg-bw-surface-subtle text-bw-ink';
  }
};

const getFeaturePriorityStyle = (priority: string) => {
  switch (priority) {
    case 'must-have': return 'bg-urgent-bg text-urgent border-red-200';
    case 'should-have': return 'bg-fit-moderate-bg text-fit-moderate border-amber-200';
    case 'nice-to-have': return 'bg-success-bg text-success border-green-200';
    default: return 'bg-bw-surface-subtle text-bw-ink border-bw-border';
  }
};

const getComponentTypeStyle = (type: string) => {
  switch (type) {
    case 'frontend': return 'bg-bw-primary-tint text-bw-primary';
    case 'backend': return 'bg-bw-primary-tint text-bw-primary';
    case 'database': return 'bg-success-bg text-success';
    case 'integration': return 'bg-orange-100 text-fit-moderate';
    case 'infrastructure': return 'bg-slate-100 text-slate-700';
    case 'service': return 'bg-teal-100 text-teal-700';
    default: return 'bg-bw-surface-subtle text-bw-ink';
  }
};

const getSolutionTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'web-application': 'Web Application',
    'mobile-app': 'Mobile App',
    'desktop-software': 'Desktop Software',
    'service-program': 'Service Program',
    'physical-deliverable': 'Physical Deliverable',
    'hybrid-solution': 'Hybrid Solution',
    'consulting-engagement': 'Consulting Engagement',
  };
  return labels[type] || type;
};

export function ProductSynthesis({
  productDefinition,
  onRegenerate,
  onEnhance,
  isRegenerating = false,
  isEnhancing = false,
  className = '',
}: ProductSynthesisProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['components', 'recommendation'])
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Handle missing data
  if (!productDefinition) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
          <Sparkles className="h-12 w-12 text-slate-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            Product Synthesis Pending
          </h3>
          <p className="text-slate-600 text-sm mb-4">
            The product definition will be synthesized from your deliverables analysis.
            This provides a high-level view of what you're proposing to deliver.
          </p>
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-bw-primary hover:brightness-110 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {isRegenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Synthesizing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Synthesize Product
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  const bidStyle = getBidRecommendationStyle(productDefinition.bid_recommendation.recommendation);
  const BidIcon = bidStyle.icon;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Product Name and Regenerate */}
      <div className="bg-bw-primary rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getDeliveryModelColor(productDefinition.delivery_model.type)} border`}>
                {getDeliveryModelIcon(productDefinition.delivery_model.type)}
                {productDefinition.delivery_model.type.toUpperCase()}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                {productDefinition.product_type}
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-3">{productDefinition.product_name}</h2>
            <p className="text-white/90 text-sm leading-relaxed">
              {productDefinition.executive_summary}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onEnhance && (
              <button
                onClick={onEnhance}
                disabled={isEnhancing || isRegenerating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
              >
                {isEnhancing ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    Enhance Section
                  </>
                )}
              </button>
            )}
            {onRegenerate && (
              <button
                onClick={onRegenerate}
                disabled={isRegenerating || isEnhancing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Synthesizing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Re-synthesize
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bid Recommendation Card */}
      <div className={`${bidStyle.bg} ${bidStyle.border} border rounded-xl p-5`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${bidStyle.bg}`}>
            <BidIcon className={`h-8 w-8 ${bidStyle.text}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className={`text-xl font-bold ${bidStyle.text} uppercase`}>
                {productDefinition.bid_recommendation.recommendation.replace('-', ' ')}
              </h3>
            </div>
            <div className="space-y-2">
              {productDefinition.bid_recommendation.rationale.map((reason, i) => (
                <p key={i} className={`text-sm ${bidStyle.text}`}>
                  • {reason}
                </p>
              ))}
            </div>
            {productDefinition.bid_recommendation.key_requirements.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-bw-muted mb-2">KEY REQUIREMENTS TO WIN:</p>
                <div className="flex flex-wrap gap-2">
                  {productDefinition.bid_recommendation.key_requirements.map((req, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-1 bg-white/50 text-bw-ink rounded text-xs">
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {productDefinition.bid_recommendation.capability_gaps.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-bw-muted mb-2">CAPABILITY GAPS:</p>
                <div className="flex flex-wrap gap-2">
                  {productDefinition.bid_recommendation.capability_gaps.map((gap, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-1 bg-urgent-bg text-urgent rounded text-xs">
                      {gap}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delivery Model */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('delivery')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
        >
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <Box className="h-5 w-5 text-bw-primary" />
            Delivery Model: {productDefinition.delivery_model.type.toUpperCase()}
          </h3>
          {expandedSections.has('delivery') ? (
            <ChevronUp className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          )}
        </button>
        {expandedSections.has('delivery') && (
          <div className="p-4 border-t border-bw-border space-y-3">
            <p className="text-bw-ink">{productDefinition.delivery_model.explanation}</p>
            <div>
              <p className="text-sm font-medium text-bw-muted mb-2">Why this model:</p>
              <ul className="space-y-1">
                {productDefinition.delivery_model.rationale.map((reason, i) => (
                  <li key={i} className="text-sm text-bw-muted flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Product Components */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('components')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
        >
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <Layers className="h-5 w-5 text-bw-primary" />
            Product Components ({productDefinition.components.length})
          </h3>
          {expandedSections.has('components') ? (
            <ChevronUp className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          )}
        </button>
        {expandedSections.has('components') && (
          <div className="p-4 border-t border-bw-border space-y-4">
            {productDefinition.components.map((component) => (
              <div key={component.id} className="border border-bw-border rounded-lg p-4 hover:border-bw-border transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-bw-muted">{component.id}</span>
                    <h4 className="font-semibold text-bw-ink">{component.name}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    {component.frequency && (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-bw-primary-tint text-bw-primary flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {component.frequency}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getComponentCategoryStyle(component.category)}`}>
                      {component.category}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-bw-muted mb-3">{component.description}</p>

                {/* User Value */}
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-4 w-4 text-success" />
                  <span className="text-sm text-success">{component.user_value}</span>
                </div>

                {/* Staffing for this component */}
                {component.staffing && (
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-success" />
                    <span className="text-sm text-success font-medium">{component.staffing}</span>
                  </div>
                )}

                {/* Key Activities */}
                {component.key_activities && component.key_activities.length > 0 && (
                  <div className="mb-3 bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                      <ListChecks className="h-3 w-3" />
                      Key Activities
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {component.key_activities.map((activity, i) => (
                        <li key={i} className="text-xs text-slate-700 flex items-start gap-1">
                          <span className="text-bw-primary mt-0.5">•</span>
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Quality Standards */}
                {component.quality_standards && component.quality_standards.length > 0 && (
                  <div className="mb-3 bg-teal-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-teal-700 mb-2 flex items-center gap-1">
                      <ClipboardCheck className="h-3 w-3" />
                      Quality Standards
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {component.quality_standards.map((standard, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded">
                          {standard}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deliverables Mapped */}
                {component.deliverables_mapped.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-bw-border">
                    <span className="text-xs text-bw-muted">Maps to:</span>
                    {component.deliverables_mapped.map((d, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========== PRODUCT SPECIFICATION SECTIONS ========== */}

      {/* Solution Overview & Final Deliverable */}
      {productDefinition.product_specification && (
        <div className="bg-bw-surface-subtle rounded-xl border border-emerald-200 overflow-hidden">
          <button
            onClick={() => toggleSection('solution')}
            className="w-full flex items-center justify-between p-4 hover:bg-success-bg/50 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Package className="h-5 w-5 text-success" />
              What We're Building
              <span className="ml-2 px-2 py-0.5 bg-success-bg text-success rounded text-sm">
                {getSolutionTypeLabel(productDefinition.product_specification.solution_overview.solution_type)}
              </span>
            </h3>
            {expandedSections.has('solution') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('solution') && (
            <div className="p-4 border-t border-emerald-200 space-y-4">
              <p className="text-bw-ink leading-relaxed">
                {productDefinition.product_specification.solution_overview.description}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-bw-muted mb-2">Key Technologies</p>
                  <div className="flex flex-wrap gap-2">
                    {productDefinition.product_specification.solution_overview.key_technologies.map((tech, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1 bg-bw-surface text-success rounded-full text-sm border border-emerald-200">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                {productDefinition.product_specification.solution_overview.deployment_model && (
                  <div>
                    <p className="text-sm font-medium text-bw-muted mb-2">Deployment Model</p>
                    <span className="inline-flex items-center px-3 py-1 bg-bw-surface text-teal-700 rounded-full text-sm border border-teal-200 capitalize">
                      <Server className="h-4 w-4 mr-1" />
                      {productDefinition.product_specification.solution_overview.deployment_model.replace('-', ' ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Final Deliverable Description */}
              <div className="bg-bw-surface rounded-lg p-4 border border-emerald-200">
                <p className="text-sm font-semibold text-success mb-2 flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  What You'll Receive at Project Completion
                </p>
                <p className="text-bw-ink">
                  {productDefinition.product_specification.final_deliverable_description}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Features with User Stories */}
      {productDefinition.product_specification?.features && productDefinition.product_specification.features.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('features')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Zap className="h-5 w-5 text-fit-moderate" />
              Features & Capabilities ({productDefinition.product_specification.features.length})
            </h3>
            {expandedSections.has('features') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('features') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              {productDefinition.product_specification.features.map((feature) => (
                <div key={feature.feature_id} className="border border-bw-border rounded-lg p-4 hover:border-amber-200 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-bw-muted">{feature.feature_id}</span>
                      <h4 className="font-semibold text-bw-ink">{feature.feature_name}</h4>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium border ${getFeaturePriorityStyle(feature.priority)}`}>
                      {feature.priority}
                    </span>
                  </div>
                  <p className="text-sm text-bw-muted mb-3">{feature.description}</p>

                  {/* User Story */}
                  <div className="bg-fit-moderate-bg rounded-lg p-3 mb-3">
                    <p className="text-xs font-semibold text-fit-moderate mb-1">User Story</p>
                    <p className="text-sm text-amber-900 italic">"{feature.user_story}"</p>
                  </div>

                  {/* Acceptance Criteria */}
                  <div>
                    <p className="text-xs font-semibold text-bw-muted mb-2">Acceptance Criteria</p>
                    <ul className="space-y-1">
                      {feature.acceptance_criteria.map((criteria, i) => (
                        <li key={i} className="text-sm text-bw-muted flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                          {criteria}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* User Interface / Key Screens */}
      {productDefinition.product_specification?.user_interface && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('screens')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Monitor className="h-5 w-5 text-bw-primary" />
              User Interface & Key Screens
            </h3>
            {expandedSections.has('screens') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('screens') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <p className="text-bw-ink">{productDefinition.product_specification.user_interface.design_approach}</p>

              {/* Design Principles */}
              <div className="flex flex-wrap gap-2">
                {productDefinition.product_specification.user_interface.design_principles.map((principle, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1 bg-bw-primary-tint text-bw-primary rounded-full text-sm">
                    {principle}
                  </span>
                ))}
              </div>

              {/* Key Screens */}
              <div className="space-y-4">
                {productDefinition.product_specification.user_interface.key_screens.map((screen) => (
                  <div key={screen.screen_id} className="border border-blue-100 rounded-lg p-4 bg-bw-primary-tint/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Monitor className="h-4 w-4 text-bw-primary" />
                      <h4 className="font-semibold text-bw-ink">{screen.screen_name}</h4>
                      <span className="text-xs font-mono text-bw-primary">{screen.screen_id}</span>
                    </div>
                    <p className="text-sm text-bw-muted mb-3">{screen.purpose}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-semibold text-bw-muted mb-1">Key Elements</p>
                        <ul className="text-sm text-bw-muted space-y-0.5">
                          {screen.key_elements.map((el, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <CircleDot className="h-3 w-3 text-bw-primary mt-1 flex-shrink-0" />
                              {el}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-bw-muted mb-1">User Actions</p>
                        <ul className="text-sm text-bw-muted space-y-0.5">
                          {screen.user_actions.map((action, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <Zap className="h-3 w-3 text-fit-moderate mt-1 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <p className="text-xs text-bw-muted mt-2">
                      <strong>Navigation:</strong> {screen.navigation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Journeys */}
      {productDefinition.product_specification?.user_journeys && productDefinition.product_specification.user_journeys.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('journeys')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Route className="h-5 w-5 text-violet-600" />
              User Journeys ({productDefinition.product_specification.user_journeys.length})
            </h3>
            {expandedSections.has('journeys') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('journeys') && (
            <div className="p-4 border-t border-bw-border space-y-6">
              {productDefinition.product_specification.user_journeys.map((journey) => (
                <div key={journey.journey_id} className="border border-violet-100 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                      {journey.actor}
                    </span>
                    <h4 className="font-semibold text-bw-ink">{journey.journey_name}</h4>
                  </div>
                  <p className="text-sm text-bw-muted mb-4">
                    <strong>Goal:</strong> {journey.goal}
                  </p>

                  {/* Journey Steps */}
                  <div className="space-y-3">
                    {journey.steps.map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-semibold text-sm">
                          {step.step_number}
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-bw-ink mb-1">{step.action}</p>
                          <p className="text-xs text-bw-muted flex items-center gap-1">
                            <ArrowRight className="h-3 w-3" />
                            {step.system_response}
                          </p>
                          {step.screen && (
                            <span className="text-xs text-violet-600 mt-1 inline-block">
                              Screen: {step.screen}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 bg-success-bg rounded-lg p-3">
                    <p className="text-sm text-success">
                      <strong>Success:</strong> {journey.success_outcome}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Architecture */}
      {productDefinition.product_specification?.architecture && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('architecture')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Server className="h-5 w-5 text-bw-primary" />
              Technical Architecture
            </h3>
            {expandedSections.has('architecture') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('architecture') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <p className="text-bw-ink">{productDefinition.product_specification.architecture.overview}</p>

              {/* System Components */}
              <div>
                <p className="text-sm font-semibold text-bw-ink mb-3">System Components</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {productDefinition.product_specification.architecture.components.map((comp) => (
                    <div key={comp.component_id} className="border border-bw-border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${getComponentTypeStyle(comp.component_type)}`}>
                          {comp.component_type}
                        </span>
                        <h5 className="font-medium text-bw-ink text-sm">{comp.component_name}</h5>
                      </div>
                      <p className="text-xs text-bw-muted mb-2">{comp.description}</p>
                      {comp.technologies && comp.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {comp.technologies.map((tech, i) => (
                            <span key={i} className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Integrations */}
              {productDefinition.product_specification.architecture.integrations.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-bw-ink mb-3 flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Integrations
                  </p>
                  <div className="space-y-2">
                    {productDefinition.product_specification.architecture.integrations.map((int) => (
                      <div key={int.integration_id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-1 bg-orange-100 text-fit-moderate rounded">{int.type}</span>
                          <span className="font-medium text-bw-ink text-sm">{int.name}</span>
                          <ArrowRight className="h-4 w-4 text-bw-muted" />
                          <span className="text-sm text-bw-muted">{int.external_system}</span>
                        </div>
                        <span className="text-xs text-bw-muted">{int.frequency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Security */}
              {productDefinition.product_specification.architecture.security_architecture &&
               productDefinition.product_specification.architecture.security_architecture.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Security Architecture
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {productDefinition.product_specification.architecture.security_architecture.map((sec, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1 bg-bw-surface text-slate-700 rounded-full text-sm border border-slate-200">
                        {sec}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Service Operations (for service contracts) */}
      {productDefinition.product_specification?.service_operations && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('operations')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Repeat className="h-5 w-5 text-teal-600" />
              Service Operations Schedule
            </h3>
            {expandedSections.has('operations') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('operations') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Daily */}
                {productDefinition.product_specification.service_operations.daily_operations.length > 0 && (
                  <div className="bg-bw-primary-tint rounded-lg p-4">
                    <p className="text-sm font-semibold text-bw-primary mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Daily Operations
                    </p>
                    <ul className="space-y-1">
                      {productDefinition.product_specification.service_operations.daily_operations.map((op, i) => (
                        <li key={i} className="text-sm text-bw-primary flex items-start gap-1">
                          <span className="text-bw-primary mt-0.5">•</span>
                          {op}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weekly */}
                {productDefinition.product_specification.service_operations.weekly_operations.length > 0 && (
                  <div className="bg-success-bg rounded-lg p-4">
                    <p className="text-sm font-semibold text-success mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Weekly Operations
                    </p>
                    <ul className="space-y-1">
                      {productDefinition.product_specification.service_operations.weekly_operations.map((op, i) => (
                        <li key={i} className="text-sm text-success flex items-start gap-1">
                          <span className="text-success mt-0.5">•</span>
                          {op}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Monthly */}
                {productDefinition.product_specification.service_operations.monthly_operations.length > 0 && (
                  <div className="bg-bw-primary-tint rounded-lg p-4">
                    <p className="text-sm font-semibold text-bw-primary mb-2 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Monthly Operations
                    </p>
                    <ul className="space-y-1">
                      {productDefinition.product_specification.service_operations.monthly_operations.map((op, i) => (
                        <li key={i} className="text-sm text-bw-primary flex items-start gap-1">
                          <span className="text-bw-primary mt-0.5">•</span>
                          {op}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Equipment & Supplies */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productDefinition.product_specification.service_operations.equipment_required &&
                 productDefinition.product_specification.service_operations.equipment_required.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Equipment Required</p>
                    <div className="flex flex-wrap gap-2">
                      {productDefinition.product_specification.service_operations.equipment_required.map((eq, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-bw-surface text-slate-600 rounded border border-slate-200">
                          {eq}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {productDefinition.product_specification.service_operations.supplies_required &&
                 productDefinition.product_specification.service_operations.supplies_required.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Supplies Required</p>
                    <div className="flex flex-wrap gap-2">
                      {productDefinition.product_specification.service_operations.supplies_required.map((sup, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-bw-surface text-slate-600 rounded border border-slate-200">
                          {sup}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== END PRODUCT SPECIFICATION SECTIONS ========== */}

      {/* User Impact */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('users')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
        >
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <Users className="h-5 w-5 text-success" />
            User Impact
          </h3>
          {expandedSections.has('users') ? (
            <ChevronUp className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          )}
        </button>
        {expandedSections.has('users') && (
          <div className="p-4 border-t border-bw-border grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-bw-muted mb-2">Primary Users</p>
              <div className="flex flex-wrap gap-2">
                {productDefinition.user_impact.primary_users.map((user, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1 bg-success-bg text-success rounded-full text-sm">
                    {user}
                  </span>
                ))}
              </div>
              {productDefinition.user_impact.user_count && (
                <p className="text-sm text-bw-muted mt-2">
                  <strong>User Count:</strong> {productDefinition.user_impact.user_count}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-bw-muted mb-2">User Benefit</p>
              <p className="text-sm text-bw-ink">{productDefinition.user_impact.user_benefit}</p>
              <p className="text-sm font-medium text-bw-muted mt-3 mb-2">Usage Scenario</p>
              <p className="text-sm text-bw-ink">{productDefinition.user_impact.usage_scenario}</p>
            </div>
          </div>
        )}
      </div>

      {/* Scope of Work */}
      {productDefinition.scope_of_work && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('scope')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-bw-primary" />
              Scope of Work
            </h3>
            {expandedSections.has('scope') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('scope') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <p className="text-bw-ink">{productDefinition.scope_of_work.overview}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm font-semibold text-success mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Included in Scope
                  </p>
                  <ul className="space-y-1">
                    {productDefinition.scope_of_work.included.map((item, i) => (
                      <li key={i} className="text-sm text-success flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {productDefinition.scope_of_work.excluded && productDefinition.scope_of_work.excluded.length > 0 && (
                  <div className="bg-urgent-bg rounded-lg p-4">
                    <p className="text-sm font-semibold text-urgent mb-2 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Excluded from Scope
                    </p>
                    <ul className="space-y-1">
                      {productDefinition.scope_of_work.excluded.map((item, i) => (
                        <li key={i} className="text-sm text-urgent flex items-start gap-2">
                          <span className="text-urgent mt-0.5">✗</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {productDefinition.scope_of_work.assumptions && productDefinition.scope_of_work.assumptions.length > 0 && (
                <div className="bg-fit-moderate-bg rounded-lg p-4">
                  <p className="text-sm font-semibold text-fit-moderate mb-2">Assumptions</p>
                  <ul className="space-y-1">
                    {productDefinition.scope_of_work.assumptions.map((item, i) => (
                      <li key={i} className="text-sm text-fit-moderate flex items-start gap-2">
                        <span className="text-fit-moderate mt-0.5">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Technical Approach */}
      {productDefinition.technical_approach && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('technical')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Wrench className="h-5 w-5 text-cyan-600" />
              Technical Approach
            </h3>
            {expandedSections.has('technical') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('technical') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <div>
                <p className="text-sm font-medium text-bw-muted mb-2">Methodology</p>
                <p className="text-bw-ink">{productDefinition.technical_approach.methodology}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productDefinition.technical_approach.tools_and_equipment && productDefinition.technical_approach.tools_and_equipment.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-bw-muted mb-2">Tools & Equipment</p>
                    <div className="flex flex-wrap gap-2">
                      {productDefinition.technical_approach.tools_and_equipment.map((tool, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {productDefinition.technical_approach.technology_stack && productDefinition.technical_approach.technology_stack.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-bw-muted mb-2">Technology Stack</p>
                    <div className="flex flex-wrap gap-2">
                      {productDefinition.technical_approach.technology_stack.map((tech, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 bg-bw-primary-tint text-bw-primary rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {productDefinition.technical_approach.innovation_highlights && productDefinition.technical_approach.innovation_highlights.length > 0 && (
                <div className="bg-bw-surface-subtle rounded-lg p-4">
                  <p className="text-sm font-semibold text-bw-primary mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Innovation Highlights
                  </p>
                  <ul className="space-y-1">
                    {productDefinition.technical_approach.innovation_highlights.map((item, i) => (
                      <li key={i} className="text-sm text-bw-primary flex items-start gap-2">
                        <span className="text-bw-primary mt-0.5">★</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Staffing Model */}
      {productDefinition.staffing_model && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('staffing')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-success" />
              Staffing Model
              {productDefinition.staffing_model.total_fte && (
                <span className="ml-2 px-2 py-0.5 bg-success-bg text-success rounded text-sm">
                  {productDefinition.staffing_model.total_fte} FTE
                </span>
              )}
            </h3>
            {expandedSections.has('staffing') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('staffing') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              {productDefinition.staffing_model.supervision_ratio && (
                <p className="text-sm text-bw-muted">
                  <strong>Supervision Ratio:</strong> {productDefinition.staffing_model.supervision_ratio}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productDefinition.staffing_model.roles.map((role, i) => (
                  <div key={i} className="border border-bw-border rounded-lg p-4 hover:border-emerald-200 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-bw-ink">{role.role}</h4>
                      <span className="px-2 py-1 bg-success-bg text-success rounded text-sm font-medium">
                        {role.quantity}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-bw-muted mb-1">Responsibilities:</p>
                        <ul className="text-sm text-bw-muted space-y-0.5">
                          {role.responsibilities.map((resp, j) => (
                            <li key={j} className="flex items-start gap-1">
                              <span className="text-success">•</span> {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {role.qualifications && role.qualifications.length > 0 && (
                        <div>
                          <p className="text-xs text-bw-muted mb-1">Qualifications:</p>
                          <div className="flex flex-wrap gap-1">
                            {role.qualifications.map((qual, j) => (
                              <span key={j} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                                {qual}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {productDefinition.staffing_model.key_personnel && productDefinition.staffing_model.key_personnel.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-bw-muted mb-2">Key Personnel</p>
                  <div className="flex flex-wrap gap-2">
                    {productDefinition.staffing_model.key_personnel.map((person, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1 bg-success-bg text-success rounded-full text-sm font-medium">
                        {person}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Service Levels */}
      {productDefinition.service_levels && productDefinition.service_levels.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('sla')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Gauge className="h-5 w-5 text-fit-moderate" />
              Service Levels / SLAs
            </h3>
            {expandedSections.has('sla') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('sla') && (
            <div className="p-4 border-t border-bw-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="px-4 py-2 text-left font-semibold text-fit-moderate">Metric</th>
                      <th className="px-4 py-2 text-left font-semibold text-fit-moderate">Target</th>
                      <th className="px-4 py-2 text-left font-semibold text-fit-moderate">Measurement</th>
                      <th className="px-4 py-2 text-left font-semibold text-fit-moderate">Reporting</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {productDefinition.service_levels.map((sla, i) => (
                      <tr key={i} className="hover:bg-orange-50/50">
                        <td className="px-4 py-3 font-medium text-bw-ink">{sla.metric_name}</td>
                        <td className="px-4 py-3 text-fit-moderate font-semibold">{sla.target}</td>
                        <td className="px-4 py-3 text-bw-muted">{sla.measurement_method}</td>
                        <td className="px-4 py-3 text-bw-muted">{sla.reporting_frequency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Implementation Plan */}
      {productDefinition.implementation_plan && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('implementation')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Calendar className="h-5 w-5 text-violet-600" />
              Implementation Plan
              {productDefinition.implementation_plan.transition_period && (
                <span className="ml-2 px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-sm">
                  {productDefinition.implementation_plan.transition_period} transition
                </span>
              )}
            </h3>
            {expandedSections.has('implementation') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('implementation') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <p className="text-bw-ink">{productDefinition.implementation_plan.approach}</p>
              <div className="space-y-4">
                {productDefinition.implementation_plan.phases.map((phase, i) => (
                  <div key={i} className="border-l-4 border-violet-400 pl-4 py-2">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-bw-ink">{phase.phase_name}</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {phase.duration}
                      </span>
                    </div>
                    <p className="text-sm text-bw-muted mb-2">{phase.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-bw-muted mb-1">Key Activities:</p>
                        <ul className="text-sm text-bw-muted space-y-0.5">
                          {phase.key_activities.map((act, j) => (
                            <li key={j} className="flex items-start gap-1">
                              <span className="text-violet-500">•</span> {act}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-bw-muted mb-1">Deliverables:</p>
                        <div className="flex flex-wrap gap-1">
                          {phase.deliverables.map((del, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 bg-violet-50 text-violet-700 rounded">
                              {del}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {productDefinition.implementation_plan.go_live_criteria && productDefinition.implementation_plan.go_live_criteria.length > 0 && (
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm font-semibold text-success mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Go-Live Criteria
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {productDefinition.implementation_plan.go_live_criteria.map((criteria, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1 bg-success-bg text-success rounded-full text-sm">
                        {criteria}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quality Assurance */}
      {productDefinition.quality_assurance && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('qa')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-teal-600" />
              Quality Assurance
            </h3>
            {expandedSections.has('qa') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('qa') && (
            <div className="p-4 border-t border-bw-border space-y-4">
              <p className="text-bw-ink">{productDefinition.quality_assurance.approach}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {productDefinition.quality_assurance.inspection_frequency && (
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-xs font-semibold text-teal-800 mb-1">Inspection Frequency</p>
                    <p className="text-sm text-teal-700">{productDefinition.quality_assurance.inspection_frequency}</p>
                  </div>
                )}
                {productDefinition.quality_assurance.quality_metrics && productDefinition.quality_assurance.quality_metrics.length > 0 && (
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-xs font-semibold text-teal-800 mb-2">Quality Metrics</p>
                    <ul className="space-y-1">
                      {productDefinition.quality_assurance.quality_metrics.map((metric, i) => (
                        <li key={i} className="text-sm text-teal-700 flex items-start gap-1">
                          <BarChart3 className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          {metric}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {productDefinition.quality_assurance.corrective_action_process && (
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-xs font-semibold text-teal-800 mb-1">Corrective Actions</p>
                    <p className="text-sm text-teal-700">{productDefinition.quality_assurance.corrective_action_process}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Success Metrics */}
      {productDefinition.success_metrics && productDefinition.success_metrics.length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
          <button
            onClick={() => toggleSection('metrics')}
            className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
          >
            <h3 className="font-semibold text-bw-ink flex items-center gap-2">
              <Activity className="h-5 w-5 text-rose-600" />
              Success Metrics / KPIs
            </h3>
            {expandedSections.has('metrics') ? (
              <ChevronUp className="h-5 w-5 text-bw-muted" />
            ) : (
              <ChevronDown className="h-5 w-5 text-bw-muted" />
            )}
          </button>
          {expandedSections.has('metrics') && (
            <div className="p-4 border-t border-bw-border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {productDefinition.success_metrics.map((metric, i) => (
                  <div key={i} className="bg-bw-surface-subtle rounded-lg p-4 border border-rose-100">
                    <p className="text-sm font-semibold text-rose-800 mb-1">{metric.metric}</p>
                    <p className="text-2xl font-bold text-rose-700 mb-1">{metric.target}</p>
                    <p className="text-xs text-rose-600">{metric.measurement}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contract Structure */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        <button
          onClick={() => toggleSection('contract')}
          className="w-full flex items-center justify-between p-4 bg-bw-surface-subtle hover:from-slate-100 transition-colors"
        >
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <FileText className="h-5 w-5 text-fit-moderate" />
            Contract Structure
          </h3>
          {expandedSections.has('contract') ? (
            <ChevronUp className="h-5 w-5 text-bw-muted" />
          ) : (
            <ChevronDown className="h-5 w-5 text-bw-muted" />
          )}
        </button>
        {expandedSections.has('contract') && (
          <div className="p-4 border-t border-bw-border space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-bw-muted mb-1">Contract Type</p>
                <p className="font-semibold text-bw-ink">{productDefinition.contract_structure.contract_type}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-bw-muted mb-1">Period of Performance</p>
                <p className="font-semibold text-bw-ink">{productDefinition.contract_structure.period_of_performance}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-bw-muted mb-1">Pricing Model</p>
                <p className="font-semibold text-bw-ink">{productDefinition.contract_structure.pricing_model}</p>
              </div>
              {productDefinition.contract_structure.option_periods && (
                <div className="bg-fit-moderate-bg rounded-lg p-3">
                  <p className="text-xs text-fit-moderate mb-1">Option Periods</p>
                  <p className="font-semibold text-fit-moderate">{productDefinition.contract_structure.option_periods}</p>
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-bw-muted mb-1">Pricing Rationale</p>
              <p className="text-sm text-bw-ink">{productDefinition.contract_structure.pricing_rationale}</p>
            </div>
          </div>
        )}
      </div>

      {/* Differentiators & Risks Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Differentiators */}
        {productDefinition.differentiators.length > 0 && (
          <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
            <div className="p-4 bg-bw-surface-subtle border-b border-bw-border">
              <h3 className="font-semibold text-bw-ink flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-success" />
                Competitive Differentiators
              </h3>
            </div>
            <div className="p-4">
              <ul className="space-y-2">
                {productDefinition.differentiators.map((diff, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-bw-ink">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    {diff}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Risks */}
        {productDefinition.risks.length > 0 && (
          <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
            <div className="p-4 bg-bw-surface-subtle border-b border-bw-border">
              <h3 className="font-semibold text-bw-ink flex items-center gap-2">
                <Shield className="h-5 w-5 text-fit-moderate" />
                Risk Assessment
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {productDefinition.risks.map((risk, i) => (
                <div key={i} className="border border-bw-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-bw-ink text-sm">{risk.risk}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRiskImpactStyle(risk.impact)}`}>
                      {risk.impact}
                    </span>
                  </div>
                  <p className="text-xs text-bw-muted">
                    <strong>Mitigation:</strong> {risk.mitigation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default ProductSynthesis;
